import { useState } from 'react'
import { updateParty } from '../../utils/partyGame'
import { MODE_NAMES, MODE_ICONS } from './modeMeta'
import PartyReadyUpHost from './PartyReadyUpHost'
import {
  IconCrown, IconCheck, IconChevronRight, IconArrowRight, IconCopy,
} from '../icons'

const MODE_LIST = Object.keys(MODE_NAMES)

export default function PartyLobby({ party, currentUserId, onEveryoneIn, onForceStart, onAbandon }) {
  // ── Hooks first — always, unconditionally. Rules of Hooks demands they run
  // in the same order on every render. Do NOT add early returns above this block.
  const [showModePicker, setShowModePicker] = useState(false)
  const [changingMode, setChangingMode] = useState(false)
  const [codeCopied, setCodeCopied] = useState(false)

  // Null-safe derived values so the effect's deps stay stable even when party
  // is null (e.g. host popup before Firestore has populated the doc).
  const players      = party?.players ? Object.entries(party.players) : []
  const playerCount  = players.length
  const isHost       = !!party && currentUserId != null && currentUserId === party.hostId
  const canStart     = playerCount >= 2
  const isPreStart   = party?.status === 'pre-start'
  const readyCount   = players.filter(([, p]) => p.ready === true).length
  const allReady     = playerCount > 0 && readyCount >= playerCount

  // ── Safe to early-return now that all hooks have been called ──────────────
  if (!party) {
    return (
      <div className="party-lobby">
        <p style={{ color: 'var(--text-muted)' }}>Loading party...</p>
      </div>
    )
  }

  // Pre-start → the polished Ready Up host screen, which owns the countdown,
  // Start Round, all-confirmed auto-start, and the un-ready drop. The plain
  // lobby below is the pre-"Everyone's In" view.
  if (isPreStart) {
    return <PartyReadyUpHost party={party} onStart={onForceStart} onAbandon={onAbandon} />
  }

  const handleModeChange = async (modeId) => {
    if (modeId === party.mode || changingMode) return
    setChangingMode(true)
    setShowModePicker(false)
    try {
      await updateParty(party.id, {
        mode: modeId,
        startActor: null,
        goalActor: null,
        pick0: null,
        pick1: null,
        actorPickerIds: [],
        directorId: null,
      })
    } finally {
      setChangingMode(false)
    }
  }

  const ModeIcon = MODE_ICONS[party.mode]

  return (
    <div className="party-lobby">
      <h1 className="party-lobby__title">ACTION! PARTY MODE</h1>

      {/* Mode badge — tappable for host, read-only for players */}
      {party.mode && (
        isHost ? (
          <button
            className="party-lobby__mode-btn"
            onClick={() => setShowModePicker((v) => !v)}
            disabled={isPreStart || changingMode}
          >
            {changingMode ? (
              <span>Changing…</span>
            ) : (
              <>
                {ModeIcon && <ModeIcon size={15} />}
                <span>{MODE_NAMES[party.mode] || party.mode}</span>
              </>
            )}
            {!isPreStart && !changingMode && (
              <IconChevronRight
                size={12}
                style={{ transform: 'rotate(90deg)', opacity: 0.7 }}
              />
            )}
          </button>
        ) : (
          <div className="party-lobby__mode-badge">
            {ModeIcon && <ModeIcon size={15} />}
            <span>{MODE_NAMES[party.mode] || party.mode}</span>
          </div>
        )
      )}

      {/* Inline mode picker (host only) */}
      {showModePicker && isHost && !isPreStart && (
        <div className="party-lobby__mode-picker">
          <p className="party-lobby__mode-picker-title">Switch Mode</p>
          <div className="party-lobby__mode-picker-grid">
            {MODE_LIST.map((id) => {
              const Icon = MODE_ICONS[id]
              const active = id === party.mode
              return (
                <button
                  key={id}
                  className={`party-lobby__mode-option${active ? ' party-lobby__mode-option--active' : ''}`}
                  onClick={() => handleModeChange(id)}
                >
                  <span className="party-lobby__mode-option__icon">
                    {Icon && <Icon size={18} />}
                  </span>
                  <span>{MODE_NAMES[id]}</span>
                  {active && (
                    <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
                      <IconCheck size={14} />
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <p className="party-lobby__join-hint">Join at action-theta.vercel.app</p>

      {/* Join code — single selectable string so a tap/drag highlights the whole
          thing for copy-paste. Letter tiles looked great but copied as spaced
          out individual characters. */}
      <div className="party-lobby__code-wrap">
        <div className="party-lobby__code" aria-label={`Game code ${party.code}`}>
          {String(party.code || '')}
        </div>
        <button
          type="button"
          className="party-lobby__copy"
          onClick={async () => {
            const code = String(party.code || '')
            if (!code) return
            try {
              await navigator.clipboard.writeText(code)
            } catch {
              // Old-browser fallback — silent on failure.
              try {
                const el = document.createElement('textarea')
                el.value = code
                el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0'
                document.body.appendChild(el)
                el.focus(); el.select()
                document.execCommand('copy')
                document.body.removeChild(el)
              } catch {}
            }
            setCodeCopied(true)
            setTimeout(() => setCodeCopied(false), 2000)
          }}
        >
          <IconCopy size={14} aria-hidden="true" />
          <span>{codeCopied ? 'Copied!' : 'Copy Code'}</span>
        </button>
      </div>

      <div className="party-lobby__player-section">
        <p className="party-lobby__player-count">
          {isPreStart
            ? `${readyCount}/${playerCount} confirmed`
            : `${playerCount} ${playerCount === 1 ? 'player' : 'players'} in lobby`}
        </p>
        <div className="party-player-grid">
          {players.map(([uid, player]) => {
            const isReady = isPreStart && player.ready
            const initial = (player.username || '?').charAt(0).toUpperCase()
            return (
              <div
                key={uid}
                className={[
                  'party-player-chip',
                  player.isHost ? 'party-player-chip--host' : '',
                  isReady ? 'party-player-chip--ready' : '',
                ].join(' ').trim()}
              >
                <div className="party-player-chip__avatar">
                  {player.avatar
                    ? <img src={player.avatar} alt={player.username} />
                    : <span className="party-avatar__initial">{initial}</span>}
                </div>
                <span className="party-player-chip__name">
                  <span>{player.username || 'Guest'}</span>
                  {player.isHost && (
                    <span className="party-crown-badge" aria-label="Host">
                      <IconCrown size={12} />
                    </span>
                  )}
                  {isPreStart && (
                    isReady
                      ? <span className="party-ready-check" aria-label="Ready"><IconCheck size={12} /></span>
                      : <span className="party-ready-dot" aria-label="Waiting" />
                  )}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {isHost && (
        <div className="party-lobby__actions">
          {isPreStart ? (
            <>
              <button
                className="btn-primary party-lobby__start-btn"
                disabled
                style={{ opacity: 0.7 }}
              >
                Confirming… {readyCount}/{playerCount} ready
              </button>
              <button
                className="btn-ghost"
                style={{ fontSize: '0.85rem' }}
                onClick={onForceStart}
              >
                Force Start
              </button>
            </>
          ) : (
            <>
              <button
                className="btn-primary party-lobby__start-btn"
                onClick={onEveryoneIn}
                disabled={!canStart}
              >
                {canStart ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    Everyone's In <IconArrowRight size={18} />
                  </span>
                ) : `Waiting for players… (${playerCount}/2 min)`}
              </button>
              <button className="btn-ghost" onClick={onAbandon} style={{ fontSize: '0.8rem' }}>
                Abandon Party
              </button>
            </>
          )}
        </div>
      )}

      {!isHost && (
        <p className="party-lobby__join-hint" style={{ marginTop: 8 }}>
          {isPreStart ? 'Confirm on your phone to start...' : 'Waiting for host to start the party...'}
        </p>
      )}
    </div>
  )
}
